import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Flame, History as HistoryIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useSessions } from "../queries/useRecords";
import { useWeakCharacters } from "../queries/useWeakCharacters";
import { cn, formatDate } from "../lib/utils";
import type { SessionGroup } from "../types/domain";

type Tab = "weak" | "records";

export function HistoryPage() {
  const [tab, setTab] = useState<Tab>("weak");
  const weak = useWeakCharacters();
  const sessions = useSessions();

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">历史</h1>
        <p className="text-sm text-[color:var(--text-muted)]">薄弱字追踪 · 全部练习记录</p>
      </div>

      <div
        role="tablist"
        aria-label="历史标签"
        className="inline-flex w-fit items-center rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] p-1 text-sm"
      >
        <TabButton active={tab === "weak"} onClick={() => setTab("weak")} icon={<Flame className="h-3.5 w-3.5" />}>
          薄弱字
        </TabButton>
        <TabButton active={tab === "records"} onClick={() => setTab("records")} icon={<HistoryIcon className="h-3.5 w-3.5" />}>
          练习记录
        </TabButton>
      </div>

      {tab === "weak" ? <WeakList query={weak} /> : <SessionsList query={sessions} />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 transition-colors",
        active
          ? "bg-[color:var(--primary)] text-[color:var(--bg)]"
          : "text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
      )}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

function WeakList({
  query,
}: {
  query: ReturnType<typeof useWeakCharacters>;
}) {
  if (query.isLoading) return <div className="text-sm text-[color:var(--text-muted)]">加载中…</div>;
  if (query.isError)
    return <div className="text-sm text-[color:var(--accent)]">加载失败：{query.error?.message}</div>;

  const rows = query.data ?? [];
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="text-sm text-[color:var(--text-muted)]">
          暂无薄弱字。完成几次练习后会自动统计。
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-3">
      {rows.map((row, idx) => (
        <Card key={`${row.character}-${row.fontStyle}-${idx}`} className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[color:var(--surface-2)] font-serif text-2xl">
            {row.character}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{row.character}</span>
              <span className="text-xs text-[color:var(--text-muted)]">{row.fontStyle}</span>
              <PriorityBadge priority={row.priority} />
            </div>
            <p className="mt-1 text-xs text-[color:var(--text-muted)]">
              平均 {Math.round(row.avgScore)} 分 · 最近 {Math.round(row.lastScore)} 分 · 失败 {row.failCount} 次
            </p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm">
              练习
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
        </Card>
      ))}
    </div>
  );
}

function PriorityBadge({ priority }: { priority: "高" | "中" | "低" }) {
  const tone =
    priority === "高"
      ? "bg-[color:var(--accent)]/10 text-[color:var(--accent)]"
      : priority === "中"
        ? "bg-[color:var(--warn)]/10 text-[color:var(--warn)]"
        : "bg-[color:var(--good)]/10 text-[color:var(--good)]";
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-medium", tone)}>{priority}</span>
  );
}

function SessionsList({
  query,
}: {
  query: ReturnType<typeof useSessions>;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (query.isLoading) return <div className="text-sm text-[color:var(--text-muted)]">加载中…</div>;
  if (query.isError)
    return <div className="text-sm text-[color:var(--accent)]">加载失败：{query.error?.message}</div>;

  const groups = query.groups ?? [];
  if (groups.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>暂无练习记录</CardTitle>
          <CardDescription>先去首页上传一张图片开始吧。</CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/">
            <Button>去首页</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-3">
      {groups.map((g) => (
        <SessionCard
          key={g.sessionId}
          group={g}
          expanded={expanded === g.sessionId}
          onToggle={() =>
            setExpanded((cur) => (cur === g.sessionId ? null : g.sessionId))
          }
        />
      ))}
    </div>
  );
}

function SessionCard({
  group,
  expanded,
  onToggle,
}: {
  group: SessionGroup;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-4">
        {group.imageUrl ? (
          <img
            src={group.imageUrl}
            alt="会话预览"
            className="h-16 w-16 shrink-0 rounded-md border border-[color:var(--border)] object-cover"
          />
        ) : (
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-[color:var(--surface-2)] text-xs text-[color:var(--text-muted)]">
            无图
          </div>
        )}
        <div className="flex-1">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-medium">{formatDate(group.createdAt)}</span>
            <span className="text-xs text-[color:var(--text-muted)]">
              {group.charCount} 字 · 平均 {group.avgScore} 分
            </span>
            {group.weakCount > 0 && (
              <span className="rounded-full bg-[color:var(--accent)]/10 px-2 py-0.5 text-[10px] font-medium text-[color:var(--accent)]">
                薄弱 {group.weakCount}
              </span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {group.characters.slice(0, 10).map((c) => (
              <ScorePill key={c.recordId} score={c.totalScore} />
            ))}
            {group.characters.length > 10 && (
              <span className="text-xs text-[color:var(--text-muted)]">
                +{group.characters.length - 10}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Link to={`/result/${group.sessionId}`}>
            <Button variant="outline" size="sm">
              查看结果
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={onToggle}>
            {expanded ? "收起" : "展开"}
          </Button>
        </div>
      </div>

      {expanded && (
        <ul className="grid gap-2 border-t border-[color:var(--border)] pt-3 sm:grid-cols-2">
          {group.characters.map((c) => (
            <li key={c.recordId}>
              <Link
                to={`/overlay/${c.recordId}`}
                className="flex items-center gap-3 rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 transition-colors hover:bg-[color:var(--surface-2)]"
              >
                <span className="font-serif text-xl">{c.character}</span>
                <span className="flex-1 text-xs text-[color:var(--text-muted)]">
                  {c.issues?.[0] ?? "无问题"}
                </span>
                <ScorePill score={c.totalScore} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ScorePill({ score }: { score: number }) {
  const v = Math.round(score);
  const tone =
    v >= 75
      ? "bg-[color:var(--good)]/10 text-[color:var(--good)]"
      : v >= 60
        ? "bg-[color:var(--warn)]/10 text-[color:var(--warn)]"
        : "bg-[color:var(--accent)]/10 text-[color:var(--accent)]";
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums", tone)}>
      {v}
    </span>
  );
}